'use client';

import Link from 'next/link';
import { Provider, Country } from '@/types';
import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getCountryFlag } from '@/lib/flags';

interface ProviderCardProps {
  provider: Provider;
  countries: Country[];
}

/**
 * Sanitize text to remove any remaining markdown or invalid content
 */
function sanitizeText(text: string): string {
  if (!text) return '';
  return text
    .replace(/!\[.*?\]\(.*?\)/g, '')  // Remove markdown images
    .replace(/\[.*?\]/g, '')           // Remove brackets
    .replace(/={3,}/g, '')             // Remove dividers
    .replace(/-{3,}/g, '')             // Remove dashes
    .replace(/blob:http[^\s]*/g, '')   // Remove blob URLs
    .replace(/\s+/g, ' ')              // Normalize spaces
    .trim();
}

export default function ProviderCard({ provider, countries }: ProviderCardProps) {
  const { t } = useLanguage();
  const providerCountries = countries.filter(c => provider.countries.includes(c.code));
  const cleanDescription = sanitizeText(provider.description);
  const shortDescription = cleanDescription.slice(0, 150) + (cleanDescription.length > 150 ? '...' : '');

  // Determine tier badge
  const getTierBadge = () => {
    switch (provider.tier) {
      case 'professional':
        return <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">{t('provider.pro')}</span>;
      case 'premium':
        return <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs font-bold px-3 py-1 rounded-full">{t('provider.premium')}</span>;
      case 'enterprise':
        return <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-3 py-1 rounded-full">{t('provider.enterprise')}</span>;
      default:
        return null;
    }
  };

  // Determine border styling for premium/enterprise
  const cardBorderClass = provider.tier === 'enterprise' || provider.tier === 'premium'
    ? 'border-2 border-[#d4a574]'
    : 'border-2 border-gray-200 dark:border-gray-700';

  return (
    <div className={`bg-white dark:bg-[#1e293b] rounded-xl ${cardBorderClass} p-6 card-hover relative transition-colors duration-300`}>
      {/* Top badges */}
      <div className="flex items-start justify-between mb-4">
        <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a5f] to-[#152a45] dark:from-[#d4a574] dark:to-[#e8c9a3] rounded-lg flex items-center justify-center">
          <span className="text-[#d4a574] dark:text-[#1e3a5f] text-2xl">🏢</span>
        </div>
        <div className="flex flex-col gap-2 items-end">
          {provider.featured && (
            <span className="bg-[#d4a574] text-white text-xs font-bold px-3 py-1 rounded-full">{t('provider.featured')}</span>
          )}
          {getTierBadge()}
          {provider.tier === 'enterprise' && (
            <span className="text-green-600 dark:text-green-400 text-xs font-semibold flex items-center gap-1">
              ✓ {t('provider.verified')}
            </span>
          )}
        </div>
      </div>

      <h4 className="text-xl font-bold text-[#1e3a5f] dark:text-white mb-2">{provider.name}</h4>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{shortDescription}</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {providerCountries.map((country) => (
          <span key={country.code} className="bg-[#1e3a5f] dark:bg-[#d4a574] text-white text-xs font-semibold px-2 py-1 rounded">
            {getCountryFlag(country.code)} {country.code}
          </span>
        ))}
      </div>

      {/* Contact info - conditional based on tier */}
      <div className="space-y-2 mb-4">
        {/* Basic tier: blur/hide contact info */}
        {provider.tier === 'basic' ? (
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-2">{t('provider.contactAvailable')}</p>
            <div className="blur-sm select-none pointer-events-none">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span>📞</span>
                <span>+XXX XXXX XXXX</span>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Professional+: show phone, email, website */}
            {provider.phone && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span>📞</span>
                <span>{provider.phone}</span>
              </div>
            )}
            {provider.email && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span>✉️</span>
                <span>{provider.email}</span>
              </div>
            )}
            {provider.website && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span>🌐</span>
                <a href={provider.website} target="_blank" rel="noopener noreferrer" className="text-[#d4a574] hover:underline">
                  {t('provider.visitWebsite')}
                </a>
              </div>
            )}
            {/* Premium+: show address */}
            {(provider.tier === 'premium' || provider.tier === 'enterprise') && provider.address && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span>📍</span>
                <span className="text-xs">{provider.address}</span>
              </div>
            )}
            {/* Premium+: show services list if available */}
            {(provider.tier === 'premium' || provider.tier === 'enterprise') && provider.services.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">{t('provider.services')}</p>
                <div className="flex flex-wrap gap-1">
                  {provider.services.slice(0, 3).map((service, idx) => (
                    <span key={idx} className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded">
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Link
        href={`/providers/${provider.id}`}
        className="w-full bg-[#d4a574] text-white py-3 rounded-lg font-semibold hover:bg-[#e8c9a3] transition block text-center"
      >
        {t('provider.viewDetails')}
      </Link>
    </div>
  );
}
